import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { API_ENDPOINTS } from '../../config/api';
import { useAppSelector } from '../../hooks/useAppSelector';
import '../../config/axiosConfig';

const DATE_FILTERS = [
  { id: 'all', label: 'All time' },
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'Week' },
  { id: '15days', label: '15 days' },
  { id: 'month', label: 'Month' },
  { id: 'custom', label: 'Custom' },
];

const todayKey = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

function formatOnboardedDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  });
}

export default function EmployeeDashboard() {
  const { user } = useAppSelector((state) => state.auth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [customFrom, setCustomFrom] = useState(todayKey());
  const [customTo, setCustomTo] = useState(todayKey());

  const perms = user?.permissions;
  const canShops = perms?.tabs?.restroLiteShops;
  const canAdd = perms?.actions?.addShop;
  const canEdit = perms?.actions?.editShop;
  const canLeads = perms?.tabs?.restroLiteLeads;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const from = dateFilter === 'custom' ? customFrom : undefined;
      const to = dateFilter === 'custom' ? customTo : undefined;
      const res = await axios.get(API_ENDPOINTS.COMPANY_EMPLOYEE_PORTAL_DASHBOARD(dateFilter, from, to));
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load dashboard');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateFilter, customFrom, customTo]);

  useEffect(() => {
    if (dateFilter === 'custom') return;
    load();
  }, [load, dateFilter]);

  const applyCustomRange = (e) => {
    e.preventDefault();
    if (!customFrom || !customTo) {
      setError('Select both start and end dates');
      return;
    }
    if (customFrom > customTo) {
      setError('Start date cannot be after end date');
      return;
    }
    load();
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-6 sm:py-8 space-y-5 sm:space-y-6 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">
              Employee portal
            </p>
            <h1 className="text-xl sm:text-2xl font-bold text-stone-900">
              Welcome{user?.name ? `, ${user.name}` : ''}
            </h1>
            {user?.empId && (
              <p className="text-sm text-stone-500 mt-1 font-mono">ID: {user.empId}</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-stone-200 p-4 shadow-sm space-y-3">
          <p className="text-sm font-semibold text-stone-800">Shops onboarded — date range</p>
          <div className="flex flex-wrap gap-2">
            {DATE_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setDateFilter(f.id)}
                className={`px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium border transition-colors ${
                  dateFilter === f.id
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {dateFilter === 'custom' && (
            <form
              onSubmit={applyCustomRange}
              className="flex flex-col sm:flex-row flex-wrap items-end gap-3 pt-1"
            >
              <label className="text-sm text-stone-600">
                From
                <input
                  type="date"
                  required
                  className="mt-1 block border border-stone-200 rounded-lg px-3 py-2"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                />
              </label>
              <label className="text-sm text-stone-600">
                To
                <input
                  type="date"
                  required
                  className="mt-1 block border border-stone-200 rounded-lg px-3 py-2"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                />
              </label>
              <button
                type="submit"
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
              >
                Apply range
              </button>
            </form>
          )}
          {data?.dateRange?.label && dateFilter !== 'all' && (
            <p className="text-xs text-stone-500">
              Showing: <span className="font-medium text-stone-700">{data.dateRange.label}</span>
              {data.dateRange.from && data.dateRange.to && data.dateRange.from !== data.dateRange.to
                ? ` (${data.dateRange.from} → ${data.dateRange.to})`
                : data.dateRange.from
                  ? ` (${data.dateRange.from})`
                  : ''}
            </p>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        {loading ? (
          <p className="text-stone-500">Loading…</p>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm">
                <p className="text-xs text-stone-500 uppercase font-semibold">Shops onboarded</p>
                <p className="text-3xl font-bold text-indigo-700 mt-1">
                  {data?.stats?.shopsOnboarded ?? 0}
                </p>
                <p className="text-xs text-stone-500 mt-1">
                  {data?.stats?.activeShops ?? 0} active in this period
                </p>
              </div>
              {canLeads && (
                <div className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm">
                  <p className="text-xs text-stone-500 uppercase font-semibold">
                    PocketBillingAI trial leads
                  </p>
                  <p className="text-3xl font-bold text-stone-800 mt-1">
                    {data?.stats?.trialLeadsTotal ?? 0}
                  </p>
                  <p className="text-xs text-stone-500 mt-1">
                    {dateFilter === 'all' ? 'All inquiries' : 'In selected period'}
                  </p>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3">
              {canShops && canAdd && (
                <Link
                  to="/employee/restro-lite-shops"
                  className="w-full sm:w-auto text-center px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                >
                  + Add shop
                </Link>
              )}
              {canShops && (
                <Link
                  to="/employee/restro-lite-shops"
                  className="w-full sm:w-auto text-center px-4 py-2.5 rounded-lg border border-stone-300 text-sm font-medium hover:bg-stone-50"
                >
                  {canEdit ? 'View & edit my shops' : 'View my shops'}
                </Link>
              )}
              {canLeads && (
                <Link
                  to="/employee/restro-lite-leads"
                  className="w-full sm:w-auto text-center px-4 py-2.5 rounded-lg border border-stone-300 text-sm font-medium hover:bg-stone-50"
                >
                  Trial leads
                </Link>
              )}
            </div>

            {data?.recentShops?.length > 0 ? (
              <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm min-w-0">
                <h2 className="px-3 sm:px-4 py-3 text-sm font-semibold text-stone-700 border-b border-stone-100">
                  Shops you onboarded
                  {dateFilter !== 'all' ? ' in this period' : ''}
                </h2>
                <ul className="divide-y divide-stone-100">
                  {data.recentShops.map((s) => (
                    <li
                      key={s.id}
                      className="px-3 sm:px-4 py-3 flex flex-col sm:flex-row sm:justify-between gap-2 sm:gap-4 text-sm"
                    >
                      <div className="min-w-0">
                        <span className="font-medium block truncate">{s.shopName}</span>
                        <span className="block text-stone-500 truncate">{s.ownerName}</span>
                      </div>
                      <div className="sm:text-right shrink-0 flex sm:flex-col justify-between sm:justify-start gap-1">
                        <span className="text-stone-500 tabular-nums">{s.mobileNumber}</span>
                        <span className="text-xs text-stone-400">
                          {formatOnboardedDate(s.onboardedAt)}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              dateFilter !== 'all' && (
                <p className="text-sm text-stone-500 bg-stone-50 border border-stone-200 rounded-lg px-4 py-6 text-center">
                  No shops onboarded in this date range.
                </p>
              )
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
